import React, { useCallback } from 'react';
import { Condition, Entity } from '../types';
import Scope from '../../../../sharedComponents/scope/Scope';
import { TAB_OPTIONS } from '../../../../sharedComponents/EntitiesDualSelectList';

interface ScopeWrapperState {
    entities: Entity[];
    conditions: Condition[];
}

interface ScopeSectionProps {
    scope: number;
    state: ScopeWrapperState;
    onChange: (next: ScopeWrapperState) => void;
    emptyStateTitle: string;
    emptyStateDescription: string;
    tabOptions?: TAB_OPTIONS[];
    addEntityTitle?: string;
}

const ScopeWrapper: React.FC<ScopeSectionProps> = ({
    scope,
    state,
    onChange,
    emptyStateTitle,
    emptyStateDescription,
    tabOptions,
    addEntityTitle
}) => {
    const { entities, conditions } = state;

    const setEntities = useCallback(
        (next: Entity[]) => onChange({ entities: next, conditions }),
        [conditions, onChange]
    );

    const setConditions = useCallback(
        (next: Condition[]) => onChange({ entities, conditions: next }),
        [entities, onChange]
    );

    const handleSelectEntities = useCallback((next: Entity[]) => setEntities(next), [setEntities]);

    const deleteEntity = useCallback(
        (e: Condition | Entity) => {
            if ('guid' in e) {
                setEntities(
                    entities.filter(
                        (en) => !(en.guid === e.guid && en.type === e.type)
                    )
                );
            } else if ('conditionString' in e) {
                setConditions(
                    conditions.filter(
                        (c) => !(c.conditionString === e.conditionString && c.conditionType === e.conditionType)
                    )
                );
            }
        },
        [entities, conditions, setEntities, setConditions]
    );

    const addNamingConvention = useCallback(
        (conditionString: string, conditionType: number) => {
            setConditions([...conditions, { conditionString, conditionType }]);
        },
        [conditions, setConditions]
    );

    const editNamingConvention = useCallback(
        (oldCondition: Condition, newCondition: Condition) => {
            setConditions(
                conditions.map((c) =>
                    c.conditionString === oldCondition.conditionString && c.conditionType === oldCondition.conditionType
                        ? newCondition
                        : c
                )
            );
        },
        [conditions, setConditions]
    );

    return (
        <Scope
            entities={entities}
            conditions={conditions}
            scope={scope}
            emptyStateTitle={emptyStateTitle}
            emptyStateDescription={emptyStateDescription}
            onAddNamingConvention={addNamingConvention}
            onEditNamingConvention={editNamingConvention}
            onSelectEntities={handleSelectEntities}
            deleteEntity={deleteEntity}
            tabOptions={tabOptions}
            addEntityTitle={addEntityTitle}
        />
    );
};

export type { ScopeWrapperState };
export default ScopeWrapper;
